import DataTable from "../../components/common/DataTable";
import { modals } from "@mantine/modals";
import { Button, TextInput, Modal, Loader, Switch, Text, Title } from "@mantine/core";
import { notifications } from "@mantine/notifications";

import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import Head from "../../components/common/Head";
import ContentTable from "../../components/movies/Table";
import useValidate from "../../hooks/useValidate";
import {
  deleteComment,
  getMovieComments,
  updateComment,
} from "../../utils/api";
import { useMovies } from "../../utils/queries";
import useStore from "../../utils/store";



const MusicVideos = ({ status }) => {
  const user = useStore((state) => state.user);
  
  const [searchParams, setSearchParams] = useSearchParams();
  const [commentsModal, setCommentsModal] = React.useState(false);
  const [selectedMusicVideo, setSelectedMusicVideo] = React.useState(null);
  const [comments, setComments] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [actionLoading, setActionLoading] = React.useState(false);
  const [searchText, setSearchText] = React.useState(
    searchParams.get("search") || ""
  );
  const [inputText, setInputText] = React.useState(
    searchParams.get("search") || ""
  );
  const [commentPagination, setCommentPagination] = React.useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const [pagination, setPagination] = React.useState({
    current: parseInt(searchParams.get("page") || "1"),
    pageSize: 10,
  });

  const {
    data: musicVideos,
    isLoading,
    refetch,
  } = useMovies(
    pagination.current,
    pagination.pageSize,
    status,
    "Music Video",
    searchText
  );

  useValidate();
  console.log("status", status);
  // Add permission check based on status
  const hasPermission = React.useMemo(() => {
    if (status === "Pending") return user?.getPendingMovies;
    if (status === "Verified") return user?.getAllMovies;
    if (status === "Rejected") return user?.getRejectedMovies;
    return user?.getAllMovies; // fallback to general permission
  }, [user, status]);

  console.log("user", user);
  // Add edit/delete permissions based on status
  const getPermissions = React.useMemo(() => {
    const permissions = {
      updateMovie: user?.updateMovie || false,
      deleteMovie: user?.deleteMovie || false,
      addMovie: user?.addMovie || false,
      isPublishAccess: user?.isPublishAccess || false,
    };

    // Update and delete permissions based on status
    if (status === "Pending") {
      permissions.updateMovie = user?.editPendingMovies || false;
      permissions.deleteMovie = user?.deletePendingMovies || false;
    } else if (status === "Verified") {
      permissions.updateMovie = user?.updateMovie || false;
      permissions.deleteMovie = user?.deleteMovie || false;
    } else if (status === "Rejected") {
      permissions.updateMovie = user?.editRejectedMovies || false;
      permissions.deleteMovie = user?.deleteRejectedMovies || false;
    }

    return permissions;
  }, [user, status]);

  const handlePaginationChange = (page, pageSize) => {
    setPagination({ current: page, pageSize: pageSize || pagination.pageSize });
    setSearchParams({
      page: page.toString(),
      ...(searchText && { search: searchText }),
    });
  };

  const handleSearch = (value) => {
    setSearchText(inputText);
    setPagination({ current: 1, pageSize: 10 });
    setSearchParams({
      page: "1",
      ...(inputText && { search: inputText }),
    });
  };

  const handleSearchClear = () => {
    setInputText("");
    setSearchText("");
    setPagination({ current: 1, pageSize: 10 });
    setSearchParams({
      page: "1",
    });
  };

  const fetchComments = async (musicVideoId, page, pageSize) => {
    setLoading(true);
    try {
      const [success, data] = await getMovieComments(
        musicVideoId,
        page,
        pageSize
      );
      if (success) {
        setComments(data.data || []);
        setCommentPagination((prev) => ({
          ...prev,
          current: page,
          pageSize,
          total: data.metadata?.totalData || 0,
        }));
      } else {
        notifications.show({ message: "Failed to load comments", color: "red" });
      }
    } catch (error) {
      notifications.show({ message: "An error occurred while loading comments", color: "red" });
    } finally {
      setLoading(false);
    }
  };

  const handleViewComments = async (musicVideo) => {
    setSelectedMusicVideo(musicVideo);
    setCommentsModal(true);
    await fetchComments(
      musicVideo._id,
      commentPagination.current,
      commentPagination.pageSize
    );
  };

  const handleCommentPaginationChange = async (page, pageSize) => {
    await fetchComments(selectedMusicVideo._id, page, pageSize);
  };

  const handleUpdateComment = async (values) => {
    setActionLoading(true);
    try {
      const [success] = await updateComment(values._id, {
        isApproved: values.isApproved,
        text: values.text,
      });
      if (success) {
        notifications.show({ message: "Comment updated successfully", color: "green" });
        await fetchComments(
          selectedMusicVideo._id,
          commentPagination.current,
          commentPagination.pageSize
        );
      } else {
        notifications.show({ message: "Failed to update comment", color: "red" });
      }
    } catch (error) {
      notifications.show({ message: "An error occurred while updating the comment", color: "red" });
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    setActionLoading(true);
    try {
      const [success] = await deleteComment(commentId);
      if (success) {
        notifications.show({ message: "Comment deleted successfully", color: "green" });
        await fetchComments(
          selectedMusicVideo._id,
          commentPagination.current,
          commentPagination.pageSize
        );
      } else {
        notifications.show({ message: "Failed to delete comment", color: "red" });
      }
    } catch (error) {
      notifications.show({ message: "An error occurred while deleting the comment", color: "red" });
    } finally {
      setActionLoading(false);
    }
  };

  const commentColumns = [
    {
      title: "User",
      dataIndex: ["user", "email"],
      key: "user",
    },
    {
      title: "Comment",
      dataIndex: "text",
      key: "text",
    },
    {
      title: "Approved",
      dataIndex: "isApproved",
      key: "isApproved",
      render: (isApproved, record) => (
        <Switch
          checked={isApproved}
          onChange={(checked) => {
            handleUpdateComment({
              ...record,
              isApproved: checked,
            });
          }}
          disabled={actionLoading}
        />
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <span onClick={() => modals.openConfirmModal({ title: "Are you sure you want to delete this comment?", labels: { confirm: "Yes", cancel: "No" }, onConfirm: () => handleDeleteComment(record._id) })}>
          <Button color="red" disabled={actionLoading}>
            Delete
          </Button>
        </span>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      
      <Head
        title={`${status || ""} Music Videos`}
        customButton={
          getPermissions.addMovie ? (
            <Link to="/music-videos/add">
              <Button>Add Music Video</Button>
            </Link>
          ) : null
        }
      />

      {/* Search Input */}
      <div className="flex justify-start mb-4">
        <TextInput
          placeholder="Search music videos by name, description, or other details..."
          allowClear
          enterButton
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          onSearch={handleSearch}
          onClear={handleSearchClear}
          style={{ width: 400 }}
        />
      </div>

      {!hasPermission ? (
        <div style={{ textAlign: "center", marginTop: "20px" }}>
          <Text type="secondary">
            You don't have permission to view {status?.toLowerCase() || ""}{" "}
            music videos
          </Text>
        </div>
      ) : (
        <ContentTable
          data={musicVideos?.data}
          loading={isLoading}
          reload={refetch}
          permissions={getPermissions}
          pagination={{
            total: musicVideos?.metadata?.totalData,
            current: pagination.current,
            pageSize: pagination.pageSize,
            onChange: handlePaginationChange,
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50, 100],
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
          }}
          currentPage={pagination.current}
          onViewComments={handleViewComments}
          exportCsvData={musicVideos?.data}
          exportCsvFilename={`music-videos-${status || "all"}-${new Date().toISOString().slice(0, 10)}.csv`}
        />
      )}
      <Modal
        title={`Comments - ${selectedMusicVideo?.name || ""}`}
        opened={commentsModal}
        onClose={() => {
          setCommentsModal(false);
          setSelectedMusicVideo(null);
          setComments([]);
          setCommentPagination({
            current: 1,
            pageSize: 10,
            total: 0,
          });
        }}
        width={800}
        footer={null}
      >
        <Loader visible={loading || actionLoading}>
          <DataTable
            columns={commentColumns}
            dataSource={comments}
            loading={loading}
            rowKey="_id"
            pagination={{
              current: commentPagination.current,
              pageSize: commentPagination.pageSize,
              total: commentPagination.total,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} comments`,
              onChange: handleCommentPaginationChange,
            }}
          />
        </Loader>
      </Modal>
    </div>
  );
};

export default MusicVideos;
